import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaArrowLeft } from 'react-icons/fa';
import { servicesCardData as defaultServicesCardData } from '../utils/servicesCardData';
import '../styles/ServiceDetail.css';

// Get merged services data (default + admin additions, excluding deleted)
const getMergedServicesData = () => {
  const savedServices = localStorage.getItem('adminServicesData');
  const deletedArtists = JSON.parse(localStorage.getItem('deletedArtists') || '{}');
  let mergedData = {};
  
  // Start with default data - deep copy and filter out deleted artists
  Object.keys(defaultServicesCardData).forEach(category => {
    const deletedIds = new Set(deletedArtists[category] || []);
    mergedData[category] = (defaultServicesCardData[category] || []).filter(
      artist => !deletedIds.has(artist.id)
    );
  });
  
  // Merge admin-added artists (also filter out deleted ones)
  if (savedServices) {
    try {
      const parsed = JSON.parse(savedServices);
      Object.keys(parsed).forEach(category => {
        const deletedIds = new Set(deletedArtists[category] || []);
        if (mergedData[category]) {
          // Merge arrays: add admin artists to default artists (avoid duplicates and deleted)
          const existingIds = new Set(mergedData[category].map(a => a.id));
          const adminArtists = (parsed[category] || []).filter(
            a => !existingIds.has(a.id) && !deletedIds.has(a.id)
          );
          mergedData[category] = [...mergedData[category], ...adminArtists];
        } else {
          // New category entirely from admin (filter deleted)
          mergedData[category] = (parsed[category] || []).filter(
            a => !deletedIds.has(a.id)
          );
        }
      });
    } catch (error) {
      console.error('Error loading services data:', error);
    }
  }
  
  return mergedData;
};

const ServiceDetail = () => {
  const { serviceName } = useParams();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [servicesCardData, setServicesCardData] = useState(getMergedServicesData());

  // Convert URL parameter to service name (handle hyphens and spaces)
  // First, try to match service names exactly by converting slug back to name
  const serviceNameMap = {
    'photoshoot-services': 'Photoshoot Services',
    'camera-gear-rentals': 'Camera & Gear Rentals',
    'casting-crew': 'Casting Crew',
    'cinema-autography': 'Cinema Autography',
    'makeup-artists': 'Makeup Artists',
    'graphic-designers': 'Graphic Designers',
    'directors': 'Directors',
    'costume-designers': 'Costume Designers',
    'aerial-videography': 'Aerial Videography',
    'documentary-production': 'Documentary Production',
    'elopement-videography': 'Elopement Videography',
    'event-video-production': 'Event Video Production',
    'family-video-productions': 'Family Video Productions',
    'film-production-services': 'Film Production Services',
    'full-service-video-production': 'Full-Service Video Production',
    'green-screen-video-services': 'Green Screen Video Services',
    'live-streaming': 'Live Streaming',
    'music-video-production': 'Music Video Production',
    'post-production-services': 'Post-Production Services',
    'pre-production-services': 'Pre-Production Services',
    'real-estate-videography': 'Real Estate Videography',
    'social-media-video-content': 'Social Media Video Content',
    'sports-videography': 'Sports Videography',
    'travel-videography': 'Travel Videography',
    'wedding-videography': 'Wedding Videography',
    'youtube-content-creation': 'YouTube Content Creation',
    'brand-video-production': 'Brand Video Production',
    'commercial-videography': 'Commercial Videography',
    'corporate-video-production': 'Corporate Video Production',
    'product-videography': 'Product Videography'
  };

  // Update services data when localStorage changes (listen for storage events)
  useEffect(() => {
    const handleStorageChange = () => {
      setServicesCardData(getMergedServicesData());
    };

    // Listen for cross-tab/window changes
    window.addEventListener('storage', handleStorageChange);
    // Listen for same-window updates (custom event from admin page)
    window.addEventListener('adminServicesUpdated', handleStorageChange);
    // Also check on focus in case data changed in another tab
    window.addEventListener('focus', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminServicesUpdated', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
    };
  }, []);

  const serviceNameFormatted = serviceNameMap[serviceName] || serviceName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const serviceCards = servicesCardData[serviceNameFormatted] || [];
  const selectedCard = serviceCards.find(card => card.id === selectedCardId) || null;

  const handleContact = (type, value) => {
    if (type === 'email') {
      window.location.href = `mailto:${value}`;
    } else if (type === 'phone') {
      window.location.href = `tel:${value}`;
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const handleCardSelect = (cardId) => {
    setSelectedCardId(cardId);
    setSelectedImage(null);
  };

  const handleBackToCards = () => {
    setSelectedCardId(null);
    setSelectedImage(null);
  };

  if (serviceCards.length === 0) {
    return (
      <div className="service-detail-page">
        <div className="service-detail-container">
          <Link to="/services" className="back-link">
            <FaArrowLeft /> Back to Services
          </Link>
          <div className="error-message">
            <h2>Service not found</h2>
            <p>The service you're looking for doesn't exist.</p>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', marginTop: '10px' }}>
              Service name: {serviceNameFormatted}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      <div className="service-detail-container">
        <Link to="/services" className="back-link">
          <FaArrowLeft /> Back to Services
        </Link>
        
        <div className="service-header">
          <h1>{serviceNameFormatted}</h1>
          <p>Choose from our professional team</p>
        </div>

        <div className="service-detail-layout">
          {/* Sidebar */}
          {serviceCards.length > 0 && (
            <div className="professionals-sidebar">
              <h2>Professionals ({serviceCards.length})</h2>
              <div className="sidebar-professionals-list">
                {serviceCards.map((card) => (
                  <div
                    key={card.id}
                    className={`sidebar-professional-item ${selectedCardId === card.id ? 'active' : ''}`}
                    onClick={() => handleCardSelect(card.id)}
                  >
                    <img src={card.image} alt={card.name} className="sidebar-professional-image" />
                    <div className="sidebar-professional-info">
                      <h4>{card.name}</h4>
                      <p>{card.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="main-content-area">
            {selectedCard ? (
              // Detailed View
              <div className="profile-card-detail">
                <button className="back-to-cards-btn" onClick={handleBackToCards}>
                  <FaArrowLeft /> Back to Profiles
                </button>
                <div className="profile-card">
                  <div className="profile-header">
                    <div className="profile-image-container">
                      <img src={selectedCard.image} alt={selectedCard.name} className="profile-image" />
                    </div>
                    <div className="profile-info">
                      <h2>{selectedCard.name}</h2>
                      <p className="profile-title">{selectedCard.title}</p>
                      <div className="profile-meta">
                        <span className="meta-item">
                          <FaMapMarkerAlt /> {selectedCard.location}
                        </span>
                        <span className="meta-item">
                          {selectedCard.experience}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="profile-body">
                    <div className="profile-section">
                      <h3>About</h3>
                      <p className="bio">{selectedCard.bio}</p>
                    </div>

                    <div className="profile-section">
                      <h3>Specialties</h3>
                      <div className="specialties-list">
                        {selectedCard.specialties.map((specialty, index) => (
                          <span key={index} className="specialty-tag">{specialty}</span>
                        ))}
                      </div>
                    </div>

                    <div className="profile-section">
                      <h3>Portfolio</h3>
                      <div className="portfolio-grid">
                        {selectedCard.portfolio.map((image, index) => (
                          <div 
                            key={index} 
                            className="portfolio-item"
                            onClick={() => handleImageClick(image)}
                          >
                            <img src={image} alt={`Portfolio ${index + 1}`} />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="profile-section contact-section">
                      <h3>Contact</h3>
                      <div className="contact-buttons">
                        <button 
                          className="contact-btn email-btn"
                          onClick={() => handleContact('email', selectedCard.email)}
                        >
                          <FaEnvelope /> {selectedCard.email}
                        </button>
                        <button 
                          className="contact-btn phone-btn"
                          onClick={() => handleContact('phone', selectedCard.phone)}
                        >
                          <FaPhone /> {selectedCard.phone}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Profile Cards Grid View
              <div className="profile-cards-grid-view">
                <h2 className="cards-view-title">Select a Professional</h2>
                <div className="profile-cards-grid">
                  {serviceCards.map((card) => (
                    <div
                      key={card.id}
                      className="profile-card-preview"
                      onClick={() => handleCardSelect(card.id)}
                    >
                      <div className="card-preview-image-container">
                        <img src={card.image} alt={card.name} />
                      </div>
                      <div className="card-preview-content">
                        <h3>{card.name}</h3>
                        <p className="card-preview-title">{card.title}</p>
                        <div className="card-preview-meta">
                          <span><FaMapMarkerAlt /> {card.location}</span>
                          <span>{card.experience}</span>
                        </div>
                        <p className="card-preview-bio">{card.bio.substring(0, 100)}...</p>
                        <div className="card-preview-specialties">
                          {card.specialties.slice(0, 3).map((specialty, index) => (
                            <span key={index} className="card-preview-specialty">{specialty}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={closeModal}>
          <span className="modal-close">&times;</span>
          <img src={selectedImage} alt="Portfolio" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default ServiceDetail;

